import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { lgTile } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { TableSkeleton, SkeletonBox } from '../components/Skeleton';
import { useRowsLand } from '../components/Fluid'; // [FLUID-4 2026-10-12] [FLUID-5 2026-10-12]
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { fetchInventoryViewSummary, InventoryViewSummary, onDataChange } from '../services/api';

/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23

/**
 * "Inventory View" — a compact, read-only roll-up of the inventory table by PM
 * for a rolling two-month window:
 *   • month1 = the current calendar month   (e.g. July)
 *   • month2 = the following month          (e.g. August)
 * The window is decided by the server, so when the month rolls over the two
 * columns advance on their own (Jul/Aug → Aug/Sep) with no change here.
 *
 * Three money columns per PM (all server-computed):
 *   • Current Month Inventory  = SUM(amount) for month1 (all rows)
 *   • New Campaigns for month2 = SUM(amount) for month2, NEW-campaign rows only
 *     (website absent from the current month — same rule as the Inventory table's
 *     Campaign Status column)
 *   • Total Inventory          = the two money columns above added together
 * Rows with a blank PM are not counted — filling in the PM on the Inventory page
 * makes that row appear here on the next refresh, which fires automatically via
 * the live data-refresh bus.
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-width: 0;
  background: ${(p) => p.theme.surface};
  /* [DOCK-CLEARANCE 2026-07-06] Clear the floating tab dock at the end of the scroll
     so the last row / Total never hides behind it. Must live on THIS (the page's own
     root) — Chromium does NOT scroll the scroll-container's (PageCard's) own
     padding-bottom into view, so that 88px alone leaves the last row covered. Same
     88px per-root fix the other in-card tabs use. */
  padding-bottom: 88px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* [INV-VIEW PHONE HEAD CENTRE 2026-09-23, user] "make this come in the middle". Phones: ONE
     centred column (title, then the "Inventory by PM …" subtitle, then "N PMs"). This replaces
     the [MOBILE 2026-07-07] two-column grid that pinned the count right, level with the
     subtitle. Desktop unchanged. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 4px;
  }
  @media (max-width: 600px) {
    padding: 10px 12px;
  }
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;

  /* [MOBILE 2026-07-07] Phones: stack the heading over the "Inventory by PM …"
     subtitle (heading on its own line, subtitle directly below) instead of the
     desktop side-by-side row. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center; /* [INV-VIEW PHONE HEAD CENTRE 2026-09-23, user] was flex-start */
    gap: 4px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;
`;

const Subtle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;

  /* [MOBILE 2026-07-07] Once stacked under the heading, let the long
     "Inventory by PM — July 2026 & August 2026" wrap on narrow phones rather
     than overflow the page width.
     [PHONE-LAYOUT 2026-10-23] ...in two even lines (it left "2026" alone on the second line at 360 px). */
  @media (max-width: 768px) {
    white-space: normal;
    text-wrap: balance;
  }
`;

const RecordCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;

const TableWrapper = styled.div`
  /* [MOTION-A 2026-08-20] Fades in when it replaces the skeleton (this wrapper is inside
     the non-loading branch, so it mounts at the handoff). Opacity only — this is the
     table's scroll container and a transform on it can break the sticky <thead>. */
  animation: pgReveal 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
  @keyframes pgReveal {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) { animation: none; }
  overflow-x: auto;
  flex: 1;
  /* [FLUID-4/5 2026-10-12, user] …and every row on screen rises in, top to bottom, as the data lands (rows only — the
     page's useRowsLand, components/Fluid.tsx landRows). */

  /* [PHONE-LAYOUT 2026-10-23] Phones: the table (wider than the screen now, see Table) swipes inside this box
     without dragging the page or the browser's back gesture along at its ends. */
  @media (max-width: 768px) {
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

// Fixed layout with four equal 25% columns (see <colgroup> in the render) so the
// column dividers line up under the four equal-width KPI cards above.
const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  /* [PHONE-LAYOUT 2026-10-23, user] Phones: four equal quarters of a 342 px screen cut every PM name and amount with
     "…" ("Amaan …", "$38,650…": 42 cut at 360 px) and stacked each heading into 6 lines. The KPI cards above are
     stacked on phones, so nothing needs the equal columns there: each column takes its longest value (a heading
     wraps at 120 px, ThLabel) and the table swipes sideways in TableWrapper. The !important beats the colgroup's
     inline 25% widths. */
  @media (max-width: 768px) {
    table-layout: auto;
    width: max-content;
    min-width: 100%;

    col {
      width: auto !important;
    }
  }
`;

const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
`;

const Th = styled.th<{ $align?: string }>`
  background: ${(p) => p.theme.pill};
  padding: 9px 12px;
  text-align: ${(p) => p.$align || 'left'};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.3;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  /* Labels are long/descriptive now ("Total Inventory for the Next month …"), so
     let them wrap onto multiple lines rather than truncate. */
  white-space: normal;
  user-select: none;
  /* Every column here is sortable; the pointer + hover brighten signal that,
     mirroring the shared DataTable header convention. */
  cursor: pointer;
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#7de5ab' : p.theme.green700)};
  }

  .sort-icon {
    flex-shrink: 0;
    margin-left: 6px;
    font-size: 10px;
    opacity: 0.5;
  }
  .sort-icon.active {
    opacity: 1;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
`;

// Keeps the sort arrow on the header's baseline beside the label and honours the
// column's alignment (right for the money columns) — same as DataTable's HeaderInner.
const HeaderInner = styled.span<{ $align?: string }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$align === 'right' ? 'flex-end' : p.$align === 'center' ? 'center' : 'flex-start')};
`;

// min-width:0 lets the label shrink and wrap inside the flex row (so the sort
// icon never gets pushed out); text-align keeps the wrapped lines flush with the
// column's numbers (right for the money columns).
const ThLabel = styled.span<{ $align?: string }>`
  min-width: 0;
  text-align: ${(p) => (p.$align === 'right' ? 'right' : p.$align === 'center' ? 'center' : 'left')};

  /* [PHONE-LAYOUT 2026-10-23] Phones: a long heading ("Total Inventory for the Next month (November 2026)") wraps at
     120 px, so its column stays about as wide as its amounts (the table's auto layout would otherwise size it to the
     whole heading on one line). */
  @media (max-width: 768px) {
    max-width: 120px;
  }
`;

const Tr = styled.tr`
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1);

  /* Row-hover tint gated by html.pointer-active (toggled in utils/hoverGate.ts)
     + hover:hover so it can never stick on a row after scrolling/reload and so
     touch devices never get a sticky highlight. */
  @media (hover: hover) {
    html.pointer-active &:hover {
      background: ${(p) =>
        p.theme.colorScheme === 'light' /* [SAGE-JADE] was a colour match that never fired */ ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'};
    }
  }
`;

const Td = styled.td<{ $align?: string; $muted?: boolean }>`
  padding: 9px 12px;
  font-size: 13px;
  color: ${(p) => (p.$muted ? p.theme.muted : p.theme.text)};
  border-bottom: 1px solid ${(p) => p.theme.line};
  text-align: ${(p) => p.$align || 'left'};
  transition: border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;

  /* [PHONE-LAYOUT 2026-10-23] Phones: a name or amount is never cut; its column widens (the table swipes). */
  @media (max-width: 768px) {
    overflow: visible;
    text-overflow: clip;
  }
`;

const PmName = styled.span`
  font-weight: 600;
`;

// Total Inventory value is colour-coded against the average: at/above the avg →
// green (same as the Highest PM KPI), below the avg → amber (same as the Lowest
// PM KPI), so the lighter-loaded PMs stand out at a glance.
const TotalCell = styled(Td)<{ $below?: boolean }>`
  font-weight: 700;
  color: ${(p) =>
    p.$below
      ? (p.theme.colorScheme === 'dark' ? '#fbbf24' : '#d97706')
      : (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
`;

/* [DOCK-CLEARANCE 2026-07-06] The Total is a NORMAL last row — deliberately NOT a
   sticky footer. A `position: sticky; bottom` footer pins to the scroll viewport's
   bottom, which sits behind the floating tab dock; offsetting it just makes rows leak
   into the gap below it mid-scroll. As an ordinary last row it scrolls to the end and
   clears the dock via AppLayout PageCard's padding-bottom (the app-wide clearance strip
   for card-scrolled pages), exactly like every other non-$flush page's last content. */
const Tfoot = styled.tfoot``;

const FootTd = styled.td<{ $align?: string }>`
  padding: 11px 12px;
  font-size: 13px;
  font-weight: 800;
  color: ${(p) => p.theme.text};
  text-align: ${(p) => p.$align || 'left'};
  background: ${(p) => p.theme.pill};
  border-top: 2px solid ${(p) => p.theme.line};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;


/* ── KPI strip (mirrors the app's liquid-glass KPI cards) ── */

const KpiBarWrap = styled.div`
  padding: 14px 20px 16px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)'
    : 'linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(241,245,249,0.78) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.08)')};
`;

const KpiBarHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;

const KpiBarTitle = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 3px;
    height: 13px;
    border-radius: 2px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
`;

const KpiBarContext = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(168px, 1fr));
  gap: 10px;

  /* [MOBILE 2026-07-07] Phones: one KPI per row (the four cards stacked one
     above another), full-width, so the Lowest/Highest PM names get room and
     stop truncating. Desktop keeps the auto-fit row. */
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const KpiCellBox = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 11px 13px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(22,25,23,0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(86,86,86,0.40) 0%, rgba(54,54,54,0.52) 100%)'
    : 'rgba(255,255,255,0.94)')};
  box-shadow:
    0 4px 14px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.28)' : 'rgba(22,25,23,0.06)')},
    inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.70)')};
  transition: transform 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 8px 20px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.36)' : 'rgba(22,25,23,0.10)')},
      inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.85)')};
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgTile}
`;

const KpiCellLabel = styled.span`
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* [PHONE-LAYOUT 2026-10-23] Phones: 11 px, the phone minimum for labels (each card is a full row there). */
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

type KpiTone = 'neutral' | 'low' | 'high' | 'count';

const KpiCellValue = styled.strong<{ $tone?: KpiTone }>`
  font-size: 18px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums;
  color: ${(p) => {
    if (p.$tone === 'low') return p.theme.colorScheme === 'dark' ? '#fbbf24' : '#d97706';
    if (p.$tone === 'high') return p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600;
    if (p.$tone === 'count') return p.theme.colorScheme === 'dark' ? '#60a5fa' : '#2563eb';
    return p.theme.text;
  }};
`;

const KpiCellSub = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Lowest/Highest PM cards put the label + value on the LEFT (as a stacked column)
// and the PM name on the RIGHT of the same row. The row is vertically centered, so
// the name sits in the middle against the FULL label+value height (equal space above
// and below) instead of dropping to the value's line. The left column is untouched;
// the name matches the value's size and ellipsises if long.
const KpiValueColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const KpiValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;

  & > ${KpiValueColumn} { flex: none; }
  & > ${KpiCellSub} {
    min-width: 0;
    text-align: right;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.15;
    color: ${(p) => p.theme.text};
  }
`;

/* Loading placeholder for the KPI bar — reuses the REAL bar/grid/cell components
   (label + value + sub) so the skeleton lays out exactly like the live bar, not
   the generic bordered <KpiSkeleton> card. */
const InventoryViewKpiSkeleton: React.FC = () => (
  <KpiBarWrap aria-busy="true" aria-label="Loading">
    <KpiBarHead>
      <SkeletonBox $w="150px" $h="12px" />
      <SkeletonBox $w="220px" $h="11px" />
    </KpiBarHead>
    <KpiGrid>
      {Array.from({ length: 4 }).map((_, i) => (
        <KpiCellBox key={i}>
          <SkeletonBox $w="70%" $h="9px" />
          <SkeletonBox $w="55%" $h="17px" />
          <SkeletonBox $w="82%" $h="9px" />
        </KpiCellBox>
      ))}
    </KpiGrid>
  </KpiBarWrap>
);

function formatMonthLabel(m: string): string {
  const [year, month] = m.split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1);
  return MONTH_LONG_YEAR_FMT.format(date);
}

function formatMoney(val: number): string {
  return `$${MONEY_FMT.format(Number(val || 0))}`;
}

// The four sortable columns, keyed to the InventoryViewRow fields.
type SortKey = 'pm' | 'month1Amount' | 'month2Amount' | 'total';

const InventoryViewPage: React.FC = () => {
  const [data, setData] = useState<InventoryViewSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // [FLUID-5 2026-10-12, user] Every row on screen rises in, top to bottom, when the table lands (components/Fluid.tsx).
  const landRef = useRef<HTMLDivElement>(null);
  useRowsLand(landRef, !loading && !error && !!data && data.rows.length > 0 ? 'land' : null);

  // Column sorting — every header is clickable. Default reproduces the original
  // view (Total Inventory, highest first). Same convention as the shared
  // DataTable: click a new column → ascending, click the active one → toggle.
  const [sort, setSort] = useState<{ sortBy: SortKey; sortOrder: 'asc' | 'desc' }>({
    sortBy: 'total',
    sortOrder: 'desc',
  });
  const handleSort = useCallback((key: SortKey) => {
    setSort((prev) => ({
      sortBy: key,
      sortOrder: prev.sortBy === key && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  // `silent` keeps the table on screen during a background refresh (e.g. a write
  // elsewhere firing the data-change bus) instead of flashing the spinner.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchInventoryViewSummary();
      setData(res);
      setError(null);
    } catch (err) {
      if (!silent) setError('Could not load inventory view.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Any successful write across the app (e.g. assigning a PM to a July row on the
  // Inventory page) refreshes this view silently so the totals stay in lockstep.
  useEffect(() => onDataChange(() => load(true)), [load]);

  // [REALTIME] 2026-06-23 — silently refresh when inventory changes (any source).
  useRealtime(['inventory'], () => load(true));

  const month1Label = data ? formatMonthLabel(data.month1) : '';
  const month2Label = data ? formatMonthLabel(data.month2) : '';

  // Column headers. month2's money column now counts only NEW campaigns (websites
  // absent from the current month, per the server), and the total sums the two
  // money columns before it. Fall back to the base label while the month window is
  // still loading so no empty "()" shows.
  const currentInvLabel = month1Label ? `Current Month Inventory (${month1Label})` : 'Current Month Inventory';
  const newCampaignsLabel = month2Label ? `New Campaigns for (${month2Label})` : 'New Campaigns';
  const totalInvLabel = month2Label ? `Total Inventory for the Next month (${month2Label})` : 'Total Inventory';

  // Rows ordered by the active column. Numeric columns fall back to an
  // alphabetical PM tiebreaker (direction-independent) so PMs sharing a value —
  // e.g. the $0 ones — keep a stable, deterministic order in either direction.
  const sortedRows = useMemo(() => {
    if (!data) return [];
    const { sortBy, sortOrder } = sort;
    const dir = sortOrder === 'asc' ? 1 : -1;
    return [...data.rows].sort((a, b) => {
      const cmp =
        sortBy === 'pm'
          ? a.pm.localeCompare(b.pm)
          : a[sortBy] - b[sortBy];
      return cmp !== 0 ? cmp * dir : a.pm.localeCompare(b.pm);
    });
  }, [data, sort]);

  // KPI strip — a load-balancing snapshot derived from the Total column. The
  // average is the "fair share" line; lowest/highest frame the spread; the
  // below-average count says how many PMs need topping up. All live off `data`.
  const kpis = useMemo(() => {
    if (!data || data.rows.length === 0) return null;
    const rows = data.rows;
    const n = rows.length;
    const totalSum = rows.reduce((s, r) => s + r.total, 0);
    const avg = totalSum / n;
    // Min/max by total; strict comparisons keep the alphabetically-first PM on a
    // tie (rows arrive alpha-sorted from the server) so the pick is deterministic.
    let low = rows[0];
    let high = rows[0];
    for (const r of rows) {
      if (r.total < low.total) low = r;
      if (r.total > high.total) high = r;
    }
    const belowAvg = rows.filter((r) => r.total < avg).length;
    return { avg, low, high, belowAvg, n };
  }, [data]);

  const kpiCells: { label: string; value: string; sub: string; tone: KpiTone; nameRight?: boolean }[] = kpis
    ? [
        { label: 'Avg Inventory / PM', value: formatMoney(kpis.avg), sub: `across ${kpis.n} PMs`, tone: 'neutral' },
        { label: 'Lowest PM', value: formatMoney(kpis.low.total), sub: kpis.low.pm, tone: 'low', nameRight: true },
        { label: 'Highest PM', value: formatMoney(kpis.high.total), sub: kpis.high.pm, tone: 'high', nameRight: true },
        { label: 'PMs Below Average', value: `${kpis.belowAvg} of ${kpis.n}`, sub: 'under the avg line', tone: 'count' },
      ]
    : [];

  // Dim double-arrow on inactive columns, bright up/down on the active one —
  // identical icon set to the shared DataTable.
  const getSortIcon = (key: SortKey) => {
    if (sort.sortBy !== key) return <i className="fas fa-sort sort-icon" aria-hidden="true" />;
    return sort.sortOrder === 'asc' ? (
      <i className="fas fa-sort-up sort-icon active" aria-hidden="true" />
    ) : (
      <i className="fas fa-sort-down sort-icon active" aria-hidden="true" />
    );
  };

  const headerColumns: { key: SortKey; label: string; align?: 'right' | 'center' }[] = [
    { key: 'pm', label: 'PM Name' },
    { key: 'month1Amount', label: currentInvLabel, align: 'center' },
    { key: 'month2Amount', label: newCampaignsLabel, align: 'center' },
    { key: 'total', label: totalInvLabel, align: 'center' },
  ];

  return (
    <Container>
      <Toolbar>
        <ToolbarLeft>
          <PageTitle>Inventory View</PageTitle>
          {data && (
            <Subtle>
              Inventory by PM — {month1Label} &amp; {month2Label}
            </Subtle>
          )}
        </ToolbarLeft>
        {data && <RecordCount>{data.rows.length} PMs</RecordCount>}
      </Toolbar>

      {!kpis && loading && <InventoryViewKpiSkeleton />}
      {kpis && (
        <KpiBarWrap>
          <KpiBarHead>
            <KpiBarTitle>Inventory Balance</KpiBarTitle>
            <KpiBarContext>{month1Label} &amp; {month2Label} · by total inventory</KpiBarContext>
          </KpiBarHead>
          <KpiGrid>
            {kpiCells.map((c) => (
              <KpiCellBox key={c.label}>
                {c.nameRight ? (
                  <KpiValueRow>
                    <KpiValueColumn>
                      <KpiCellLabel>{c.label}</KpiCellLabel>
                      <KpiCellValue $tone={c.tone}>{c.value}</KpiCellValue>
                    </KpiValueColumn>
                    <KpiCellSub title={c.sub}>{c.sub}</KpiCellSub>
                  </KpiValueRow>
                ) : (
                  <>
                    <KpiCellLabel>{c.label}</KpiCellLabel>
                    <KpiCellValue $tone={c.tone}>{c.value}</KpiCellValue>
                    <KpiCellSub title={c.sub}>{c.sub}</KpiCellSub>
                  </>
                )}
              </KpiCellBox>
            ))}
          </KpiGrid>
        </KpiBarWrap>
      )}

      {loading ? (
        <TableSkeleton
          columns={[
            { label: 'PM Name' },
            { label: currentInvLabel, align: 'center' },
            { label: newCampaignsLabel, align: 'center' },
            { label: totalInvLabel, align: 'center' },
          ]}
        />
      ) : error ? (
        // [LOAD-STATES 2026-09-29, user] Was a plain line with no retry.
        <LoadFailed what="inventory view" onRetry={() => load()} />
      ) : !data || data.rows.length === 0 ? (
        <EmptyState title={`No inventory for ${month1Label} or ${month2Label}`} hint="It fills in as campaigns are added for these months." />
      ) : (
        <TableWrapper ref={landRef}>
          <Table>
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '25%' }} />
            </colgroup>
            <Thead>
              <tr>
                {headerColumns.map((col) => (
                  <Th
                    key={col.key}
                    $align={col.align}
                    onClick={() => handleSort(col.key)}
                    aria-sort={
                      sort.sortBy === col.key
                        ? sort.sortOrder === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <HeaderInner $align={col.align}>
                      <ThLabel $align={col.align}>{col.label}</ThLabel>
                      {getSortIcon(col.key)}
                    </HeaderInner>
                  </Th>
                ))}
              </tr>
            </Thead>
            <tbody>
              {sortedRows.map((r) => (
                <Tr key={r.pm}>
                  <Td><PmName>{r.pm}</PmName></Td>
                  <Td $align="center" $muted={r.month1Amount === 0}>{formatMoney(r.month1Amount)}</Td>
                  <Td $align="center" $muted={r.month2Amount === 0}>{formatMoney(r.month2Amount)}</Td>
                  <TotalCell $align="center" $below={kpis != null && r.total < kpis.avg}>{formatMoney(r.total)}</TotalCell>
                </Tr>
              ))}
            </tbody>
            <Tfoot>
              <tr>
                <FootTd>Total</FootTd>
                <FootTd $align="center">{formatMoney(data.totals.month1Amount)}</FootTd>
                <FootTd $align="center">{formatMoney(data.totals.month2Amount)}</FootTd>
                <FootTd $align="center">{formatMoney(data.totals.total)}</FootTd>
              </tr>
            </Tfoot>
          </Table>
        </TableWrapper>
      )}
    </Container>
  );
};

export default InventoryViewPage;
